import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { achievementManager, Achievement } from '../utils/achievementManager';

interface AchievementPanelProps {
  className?: string;
  modeId?: string;
}

const PanelContainer = styled.div`
  --ap-bg: var(--theme-card-background, rgba(255,255,255,0.08));
  --ap-border: var(--theme-card-border, rgba(255,255,255,0.18));
  --ap-text: var(--theme-text, #f5f5f5);
  --ap-muted: color-mix(in srgb, var(--ap-text) 60%, transparent);
  background: var(--ap-bg);
  backdrop-filter: blur(10px) saturate(160%);
  border: 1px solid var(--ap-border);
  border-radius: 16px;
  padding: 14px 14px 18px;
  color: var(--ap-text);
  width: 100%;
  max-width: 300px; /* base width */
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 6px 18px -4px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.25);
  transition: max-width .3s ease, backdrop-filter .3s ease;

  @media (min-width: 1100px) {
    max-width: 260px; /* about ~15% slimmer on large */
  }
  @media (min-width: 1400px) {
    max-width: 225px; /* ~25% slimmer compared to original 300px */
  }
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  text-align: center;
  color: var(--ap-text);
`;

const AchievementItem = styled.div<{ $isUnlocked: boolean; $rarity: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: ${props => props.$isUnlocked 
    ? `linear-gradient(135deg, ${getRarityColor(props.$rarity)}20, ${getRarityColor(props.$rarity)}10)`
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 1px solid ${props => props.$isUnlocked 
    ? getRarityColor(props.$rarity) 
    : 'rgba(255, 255, 255, 0.1)'
  };
  opacity: ${props => props.$isUnlocked ? 1 : 0.6};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$isUnlocked 
      ? `linear-gradient(135deg, ${getRarityColor(props.$rarity)}30, ${getRarityColor(props.$rarity)}15)`
      : 'rgba(255, 255, 255, 0.1)'
    };
  }
`;

const Icon = styled.div<{ $isUnlocked: boolean }>`
  font-size: 24px;
  filter: ${props => props.$isUnlocked ? 'none' : 'grayscale(1)'};
  min-width: 24px;
`;

const Content = styled.div`
  flex: 1;
`;

const AchievementName = styled.div<{ $isUnlocked: boolean }>`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
  color: ${p => p.$isUnlocked ? 'var(--ap-text)' : 'var(--ap-muted)'};
`;

const AchievementDescription = styled.div`
  font-size: 12px;
  line-height: 1.3;
  color: var(--ap-muted);
`;

const RarityBadge = styled.div<{ $rarity: string; $isUnlocked: boolean }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$isUnlocked ? getRarityColor(props.$rarity) : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  text-transform: uppercase;
  font-weight: bold;
  min-width: fit-content;
`;

const Stats = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  text-align: center;
`;

const StatItem = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
`;

const StatValue = styled.span`
  font-weight: bold;
  color: #4CAF50;
`;

function getRarityColor(rarity: string): string {
  switch (rarity) {
    case 'legendary': return '#FFD700';
    case 'epic': return '#9C27B0';
    case 'rare': return '#2196F3';
    default: return '#4CAF50';
  }
}

function getRarityText(rarity: string): string {
  switch (rarity) {
    case 'legendary': return 'Legendarisk';
    case 'epic': return 'Episk';
    case 'rare': return 'Sällsynt';
    default: return 'Vanlig';
  }
}

const AchievementPanel: React.FC<AchievementPanelProps> = ({ className, modeId }) => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [unlockedCount, setUnlockedCount] = useState(0);

  useEffect(() => {
    if (modeId) achievementManager.setMode(modeId);
    const allAchievements = achievementManager.getAllAchievements();
    const unlocked = achievementManager.getUnlockedAchievements();
    
    setAchievements(allAchievements);
    setUnlockedCount(unlocked.length);
  }, [modeId]);

  // Uppdatera när nya achievements låses upp
  useEffect(() => {
    const checkForUpdates = () => {
      const unlocked = achievementManager.getUnlockedAchievements();
      setUnlockedCount(unlocked.length);
    };

    const interval = setInterval(checkForUpdates, 10000);
    return () => clearInterval(interval);
  }, []);

  const unlockedAchievementIds = useMemo(() => {
    return achievementManager.getUnlockedAchievements().map(a => a.id);
  }, []);

  return (
    <PanelContainer className={className}>
      <Title>🏆 Prestationer</Title>
      
      <Stats>
        <StatItem>
          Upplåsta: <StatValue>{unlockedCount}</StatValue>/{achievements.length}
        </StatItem>
        <StatItem>
          Framsteg: <StatValue>{achievements.length ? Math.round((unlockedCount / achievements.length) * 100) : 0}%</StatValue>
        </StatItem>
      </Stats>

      {achievements.map(achievement => {
        const isUnlocked = unlockedAchievementIds.includes(achievement.id);
        return (
          <AchievementItem 
            key={achievement.id} 
            $isUnlocked={isUnlocked}
            $rarity={achievement.rarity}
          >
            <Icon $isUnlocked={isUnlocked}>
              {achievement.icon}
            </Icon>
            <Content>
              <AchievementName $isUnlocked={isUnlocked}>
                {achievement.name}
              </AchievementName>
              <AchievementDescription>
                {achievement.description}
              </AchievementDescription>
            </Content>
            <RarityBadge $rarity={achievement.rarity} $isUnlocked={isUnlocked}>
              {getRarityText(achievement.rarity)}
            </RarityBadge>
          </AchievementItem>
        );
      })}
    </PanelContainer>
  );
};

export default AchievementPanel;
